<template>
  <v-navigation-drawer
    :value="value"
    @input="$emit('input', $event)"
    right
    fixed
    temporary
    width="420"
    class="ready-to-hire-filter"
  >
    <div class="d-flex flex-column h-100">
      <div class="d-flex align-items-center justify-content-between p-6">
        <h3 class="font-weight-bolder text-dark mb-0">Filter</h3>
        <i
          @click.stop="$emit('input', false)"
          class="flaticon-cancel text-dark font-weight-bold cursor-pointer"
        ></i>
      </div>
      <v-progress-linear v-if="optionsLoading" indeterminate color="info" />

      <div class="flex-grow-1 overflow-auto px-6">
        <div class="alert alert-light-info font-size-sm py-3 mb-6">
          Talents already being worked (contacted, interested, nominated or
          marked Not Relevant) stay in the list whatever the filters.
        </div>

        <label class="ready-to-hire-filter__label">Seniority Level</label>
        <v-select
          v-model="form.seniority_level"
          :items="options?.seniority_levels || []"
          item-text="name"
          item-value="id"
          placeholder="Select Seniority Level"
          clearable
          dense
          outlined
        />

        <label class="ready-to-hire-filter__label">Industry</label>
        <v-autocomplete
          v-model="form.industry"
          :items="projectTypes"
          item-text="name"
          item-value="id"
          placeholder="Select Industry"
          clearable
          dense
          outlined
        />

        <template v-if="options?.primary_technical_languages">
          <label class="ready-to-hire-filter__label">Primary Language</label>
          <v-autocomplete
            v-model="form.primary_technical_language"
            :items="options.primary_technical_languages"
            item-text="name"
            item-value="id"
            placeholder="Select Primary Language"
            clearable
            dense
            outlined
          />
        </template>

        <template v-if="options?.secondary_technical_languages">
          <label class="ready-to-hire-filter__label">Secondary Language</label>
          <v-autocomplete
            v-model="form.secondary_technical_language"
            :items="options.secondary_technical_languages"
            item-text="name"
            item-value="id"
            placeholder="Select Secondary Language"
            clearable
            dense
            outlined
          />
        </template>

        <template v-if="options?.skills">
          <label class="ready-to-hire-filter__label">Skills</label>
          <v-autocomplete
            v-model="form.skills"
            :items="options.skills"
            item-text="name"
            item-value="id"
            placeholder="Select Skills"
            multiple
            small-chips
            deletable-chips
            dense
            outlined
          />
        </template>
        <p v-else class="text-muted font-size-sm">
          Pick a role to filter by skills.
        </p>

        <label class="ready-to-hire-filter__label">Country</label>
        <v-autocomplete
          v-model="form.countries"
          :items="options?.countries || []"
          item-text="name"
          item-value="id"
          placeholder="Select Country"
          multiple
          small-chips
          deletable-chips
          dense
          outlined
        />

        <label class="ready-to-hire-filter__label">Availability</label>
        <div class="d-flex flex-wrap mb-4">
          <v-checkbox
            v-for="option in availabilities"
            :key="option.id"
            v-model="form.availability"
            :value="option.id"
            :label="
              counts ? `${option.name} (${counts[option.count]})` : option.name
            "
            class="mt-0 mr-6"
            hide-details
            dense
          />
        </div>

        <label class="ready-to-hire-filter__label">Spoken Language</label>
        <v-autocomplete
          v-model="form.language"
          :items="options?.languages || []"
          item-text="name"
          item-value="id"
          placeholder="Select Language"
          clearable
          dense
          outlined
        />

        <label class="ready-to-hire-filter__label">Language Level</label>
        <v-select
          v-model="form.language_level"
          :items="options?.language_levels || []"
          item-text="name"
          item-value="id"
          placeholder="Select Language Level"
          :disabled="!form.language"
          clearable
          dense
          outlined
        />

        <v-checkbox
          v-model="form.qualified"
          label="Qualified talents only"
          class="mt-0"
          hide-details
          dense
        />
      </div>

      <div
        class="d-flex align-items-center justify-content-between p-6 border-top"
      >
        <div class="font-weight-bolder">
          Total number:
          <span v-if="counts">{{ counts.total }}</span>
          <v-progress-circular
            v-if="countsLoading"
            indeterminate
            size="14"
            width="2"
            class="ml-1"
          />
        </div>
        <div>
          <button type="button" class="btn btn-light mr-2" @click="reset">
            Reset
          </button>
          <button type="button" class="btn btn-info" @click="apply">
            Apply
          </button>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";
import {
  READY_TO_HIRE_AVAILABILITIES,
  emptyReadyToHireFilters,
  readyToHireFilterParams,
} from "@/constants/ready-to-hire-filters";

export default {
  name: "ReadyToHireFilterDrawer",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: Number,
      required: true,
    },
    // The role id the list is narrowed to; null for every role
    role: {
      type: Number,
      default: null,
    },
    // The filters the list is narrowed by
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: emptyReadyToHireFilters(),
      availabilities: READY_TO_HIRE_AVAILABILITIES,
      options: null,
      // The role the options were loaded for; they only change with it
      optionsRole: undefined,
      optionsLoading: false,
      counts: null,
      countsLoading: false,
      // Bumped per counts request, so only the latest response shows.
      countsSeq: 0,
    };
  },
  computed: {
    ...mapGetters({
      projectTypes: "shared/getProjectTypes",
    }),
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.open();
    },
    form: {
      deep: true,
      handler() {
        if (this.value) this.fetchCountsSoon();
      },
    },
  },
  created() {
    this.fetchCountsSoon = debounce(this.fetchCounts, 400);
  },
  beforeDestroy() {
    this.fetchCountsSoon.cancel();
  },
  methods: {
    ...mapActions({
      getProjectTypes: "shared/getProjectTypes",
      getReadyToHireFilterOptions: "clients/getReadyToHireFilterOptions",
      getReadyToHireFilterCounts: "clients/getReadyToHireFilterCounts",
    }),
    open() {
      // Edited apart from the list's filters until Apply
      this.form = JSON.parse(JSON.stringify(this.filters));
      this.getProjectTypes();
      if (this.optionsRole !== this.role) this.fetchOptions();
    },
    fetchOptions() {
      const role = this.role;
      this.optionsLoading = true;
      this.getReadyToHireFilterOptions({ project_id: this.projectId, role })
        .then((res) => {
          if (role !== this.role) return;

          this.options = res.data.data;
          this.optionsRole = role;
        })
        // The axios interceptor already shows the error.
        .catch(() => {})
        .finally(() => {
          this.optionsLoading = false;
        });
    },
    fetchCounts() {
      const seq = ++this.countsSeq;
      this.countsLoading = true;
      this.getReadyToHireFilterCounts({
        project_id: this.projectId,
        role: this.role,
        filters: readyToHireFilterParams(this.form),
      })
        .then((res) => {
          if (seq === this.countsSeq) this.counts = res.data.data;
        })
        // The axios interceptor already shows the error.
        .catch(() => {})
        .finally(() => {
          if (seq === this.countsSeq) this.countsLoading = false;
        });
    },
    apply() {
      this.$emit("apply", JSON.parse(JSON.stringify(this.form)));
      this.$emit("input", false);
    },
    reset() {
      this.form = emptyReadyToHireFilters();
      this.$emit("apply", emptyReadyToHireFilters());
    },
  },
};
</script>

<style scoped>
.ready-to-hire-filter__label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #3f4254;
}
</style>
